/* ═══════════════════════════════════════════════════════════════
   useSharedForma — THE binding of a forma to its shared live draft
   (Sync appendix items 7–9). One binding, two hosts:

     · the Master Forma Studio (useMasterFormaDraft) — a master forma;
     · the Designer (forma-studio/useFormaDraft) — a project forma copy.

   Everyone who opens a forma edits one Yjs document (`forma:<id>`,
   formaCollab.ts); the server keeps it on every change. The published JSON
   columns the Formatter, exports and Apply-to-spec read are written ONLY by
   Publish (a `collabMirror` write the server gates on `formas.publish`).

   The host owns WHICH row is open and its published copy (how it loads it
   differs); this hook owns everything about the document: the socket, the
   one-seat seeding, the forma that follows the document, Yjs undo, Publish,
   Discard draft, the end of a document (4409 / 4410), peers and the chip.
   The pure decisions live in formaDraftModel.ts.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type * as Y from 'yjs';
import type { StandardConfig } from '../../shared/types';
import { FormaService } from '../services/formaService';
import { reportError } from '../services/errorLog';
import { useCollabDoc } from '../spec-studio/useCollabDoc';
import {
  collabDocName,
  shouldSeed,
  type CollabPeer,
  type CollabStatus,
  type LiveBlock,
} from '../spec-studio/collabState';
import {
  FORMA_META,
  FORMA_ROOT,
  createFormaUndoManager,
  formaMeta,
  isFormaDirty,
  isFormaSeeded,
  readForma,
  seedForma,
} from './formaCollab';
import {
  FORMA_UNDO_LIMIT,
  applyLocalEdit,
  deriveFormaChip,
  normalizeEos,
  peerSelectionsFromAwareness,
  publishFromDoc,
  publishSaveOptions,
  stripDerived,
  type FormaChipState,
} from './formaDraftModel';

export interface SharedFormaOpts {
  /** The row whose shared document is open; null = `localDoc` (no row yet). */
  liveId: string | null;
  /** A brand-new / imported forma's local document (no socket). */
  localDoc?: Y.Doc | null;
  /** The PUBLISHED forma as the host last read it — the seed source. */
  published: StandardConfig | null;
  /** The host has the published row: the socket may open. */
  ready: boolean;
  userName: string;
  userId: string | undefined;
  /** Edit mode (false = View: inert, not announced). */
  editing: boolean;
  /** formas.edit — may write the socket / seed. */
  canEdit: boolean;
  /** formas.publish. */
  canPublish: boolean;
  /** Re-read the published row after the document ended (host-owned). */
  reloadPublished: () => Promise<void>;
  /** A colleague published: the published row moved. */
  onPublishedRow: (row: StandardConfig) => void;
  /** Our Publish landed (the host records it; `live` = it was a shared draft). */
  onPublished?: (saved: StandardConfig, live: boolean) => void;
  /** The shared document ended under this window. */
  onEnded?: (info: { reset: boolean; own: boolean }) => void;
  /** False until a brand-new forma's first Publish ('created' history line). */
  savedOnceRef: React.MutableRefObject<boolean>;
}

export interface SharedForma {
  ydoc: Y.Doc | null;
  /** The forma as the document holds it (null until seeded). */
  docForma: StandardConfig | null;
  dirty: boolean;
  saving: boolean;
  edit: (fn: (f: StandardConfig) => StandardConfig) => boolean;
  undo: () => boolean;
  redo: () => boolean;
  canUndo: boolean;
  canRedo: boolean;
  publish: () => Promise<StandardConfig | undefined>;
  discardDraft: () => Promise<boolean>;
  /** Close the document and re-open it on a fresh read of the row. */
  reopen: () => void;
  peers: CollabPeer[];
  status: CollabStatus;
  chip: FormaChipState;
  blocked: LiveBlock | null;
  synced: boolean;
  peerSelections: Record<string, number>;
  setSelection: (keys: string[]) => void;
}

export function useSharedForma(o: SharedFormaOpts): SharedForma {
  const { liveId, localDoc = null, published, ready, userName, userId } = o;
  const { editing, canEdit, canPublish } = o;
  const optsRef = React.useRef(o);
  optsRef.current = o;

  const [docForma, setDocForma] = React.useState<StandardConfig | null>(null);
  const [dirty, setDirty] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [epoch, setEpoch] = React.useState(0);
  // The document ended and the published row is being re-read: no socket
  // until it lands, or the new document would seed from the OLD row.
  const [reopening, setReopening] = React.useState(false);
  const [undoTick, setUndoTick] = React.useState(0);
  const [peerSel, setPeerSel] = React.useState<Record<string, number>>({});

  const origin = React.useMemo(() => ({ forma: 'local-edit' }), []);
  const publishedRef = React.useRef<StandardConfig | null>(published);
  publishedRef.current = published;
  const docFormaRef = React.useRef<StandardConfig | null>(null);
  const ydocRef = React.useRef<Y.Doc | null>(null);
  const umRef = React.useRef<Y.UndoManager | null>(null);
  const discardingRef = React.useRef(false);
  const liveIdRef = React.useRef(liveId);
  liveIdRef.current = liveId;

  const collab = useCollabDoc(
    liveId && ready && !reopening ? collabDocName('forma', liveId) : null,
    userName,
    { announce: editing && canEdit, epoch }
  );
  const collabRef = React.useRef(collab);
  collabRef.current = collab;

  const ydoc = liveId ? (collab?.ydoc ?? null) : localDoc;

  const reopen = React.useCallback(() => {
    setReopening(true);
    setEpoch((e) => e + 1);
    void optsRef.current
      .reloadPublished()
      .catch(() => undefined)
      .finally(() => setReopening(false));
  }, []);

  // First writer seeds an empty shared document from the published row —
  // only the ONE seat the server named, only after the first sync.
  const trySeed = React.useCallback(() => {
    const c = collabRef.current;
    const pub = publishedRef.current;
    const d = ydocRef.current;
    if (!c || !pub || !d || c.ydoc !== d || !liveIdRef.current) return;
    const meta = formaMeta(d);
    const seeder = meta.get('seeder');
    if (
      shouldSeed({
        synced: c.synced,
        readOnly: !canEdit,
        fragmentEmpty: d.getMap(FORMA_ROOT).size === 0,
        seeded: isFormaSeeded(d),
        seeder: typeof seeder === 'string' ? seeder : null,
        mySeat: c.seat,
      })
    ) {
      seedForma(d, stripDerived(pub), 'seed', c.seat);
    }
  }, [canEdit]);
  const trySeedRef = React.useRef(trySeed);
  trySeedRef.current = trySeed;
  React.useEffect(() => {
    trySeed();
  }, [trySeed, collab?.synced, collab?.ydoc, published]);

  // Bind the document: the forma FOLLOWS it (local and remote updates take
  // the same path), the undo manager, the seeder watch.
  React.useEffect(() => {
    ydocRef.current = ydoc;
    docFormaRef.current = null;
    setDocForma(null);
    setDirty(false);
    if (!ydoc) return;
    const root = ydoc.getMap(FORMA_ROOT);
    const meta = ydoc.getMap(FORMA_META);
    const um = createFormaUndoManager(ydoc, origin);
    umRef.current = um;
    const onStack = () => {
      while (um.undoStack.length > FORMA_UNDO_LIMIT) um.undoStack.shift();
      setUndoTick((n) => n + 1);
    };
    um.on('stack-item-added', onStack);
    um.on('stack-item-popped', onStack);
    let wasDirty = isFormaDirty(ydoc);
    const refresh = () => {
      trySeedRef.current();
      if (!isFormaSeeded(ydoc)) return;
      const f = normalizeEos(stripDerived(readForma(ydoc)));
      docFormaRef.current = f;
      setDocForma(f);
      const d = isFormaDirty(ydoc);
      setDirty(d);
      // A colleague published (pending -> clean): the published row moved.
      if (wasDirty && !d && liveIdRef.current) {
        void FormaService.getFresh(liveIdRef.current)
          .then((row) => {
            if (row && ydocRef.current === ydoc) optsRef.current.onPublishedRow(stripDerived(row));
          })
          .catch(() => undefined);
      }
      wasDirty = d;
    };
    root.observeDeep(refresh);
    meta.observe(refresh);
    refresh();
    return () => {
      root.unobserveDeep(refresh);
      meta.unobserve(refresh);
      um.destroy();
      if (umRef.current === um) umRef.current = null;
    };
  }, [ydoc, origin]);

  // The shared document ended under us (4409 / 4410, or our own Discard):
  // re-read the published forma and open a fresh document.
  const endedHandled = React.useRef<Y.Doc | null>(null);
  React.useEffect(() => {
    if (!collab?.discarded || endedHandled.current === collab.ydoc) return;
    endedHandled.current = collab.ydoc;
    const own = discardingRef.current;
    discardingRef.current = false;
    optsRef.current.onEnded?.({ reset: collab.reset, own });
    reopen();
  }, [collab?.discarded, collab?.reset, collab?.ydoc, reopen]);

  // Who else is here, and what they last touched.
  React.useEffect(() => {
    const aw = collab?.provider.awareness;
    if (!collab || !aw) {
      setPeerSel((p) => (Object.keys(p).length ? {} : p));
      return;
    }
    const upd = () => {
      const next = peerSelectionsFromAwareness(
        aw.getStates().entries(),
        collab.ydoc.clientID,
        userName
      );
      setPeerSel((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    };
    aw.on('change', upd);
    upd();
    return () => aw.off('change', upd);
  }, [collab, userName]);

  const setSelection = React.useCallback((keys: string[]) => {
    collabRef.current?.provider.awareness?.setLocalStateField('selection', keys);
  }, []);

  const edit = React.useCallback(
    (fn: (f: StandardConfig) => StandardConfig): boolean => {
      const d = ydocRef.current;
      const cur = docFormaRef.current;
      if (!d || !cur || !canEdit || !editing || collabRef.current?.blocked) return false;
      // Applied synchronously: the observer refreshes docFormaRef before the
      // next edit in the same tick reads it.
      applyLocalEdit(d, cur, fn, origin);
      return true;
    },
    [origin, canEdit, editing]
  );

  const undo = React.useCallback((): boolean => {
    const um = umRef.current;
    if (!um || !editing) return false;
    return !!um.undo();
  }, [editing]);

  const redo = React.useCallback((): boolean => {
    const um = umRef.current;
    if (!um || !editing) return false;
    return !!um.redo();
  }, [editing]);

  const publish = React.useCallback(async (): Promise<StandardConfig | undefined> => {
    const d = ydocRef.current;
    if (!d || !docFormaRef.current) return undefined;
    const live = !!liveIdRef.current;
    if (live && !canPublish) throw new Error('You do not have permission to publish formas');
    setSaving(true);
    try {
      const saved = await publishFromDoc(d, {
        userName,
        userId,
        savedOnce: optsRef.current.savedOnceRef.current,
        // Creating the row (no shared draft yet) is an ordinary write;
        // publishing a live draft is the flagged mirror write.
        save: (cfg) => FormaService.save(cfg, publishSaveOptions(live)),
      });
      optsRef.current.savedOnceRef.current = true;
      optsRef.current.onPublished?.(saved, live);
      return saved;
    } catch (err) {
      reportError('Forma:publish', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [userName, userId, canPublish]);

  // Discard = write the PUBLISHED columns back WITHOUT the mirror flag. The
  // server reads an unflagged write of those columns as "the row changed
  // outside the editor": ydoc_state is nulled in the same statement and every
  // connected editor is closed with 4410 and reloads the published forma.
  const discardDraft = React.useCallback(async (): Promise<boolean> => {
    const id = liveIdRef.current;
    if (!id) return false;
    discardingRef.current = true;
    try {
      const fresh = await FormaService.getFresh(id);
      if (!fresh) throw new Error('This forma could not be found');
      await FormaService.save(stripDerived(fresh));
      return true;
    } catch (err) {
      discardingRef.current = false;
      reportError('Forma:discardDraft', err);
      throw err;
    }
  }, []);

  const live = !!liveId;
  const status: CollabStatus = live ? (collab?.status ?? 'connecting') : 'connected';
  const chip = deriveFormaChip({
    live,
    status,
    ready: live ? !!collab?.synced : true,
    unsyncedChanges: collab?.unsyncedChanges ?? 0,
    publishing: saving,
    dirty,
  });
  void undoTick;

  return {
    ydoc,
    docForma,
    dirty,
    saving,
    edit,
    undo,
    redo,
    canUndo: (umRef.current?.undoStack.length ?? 0) > 0,
    canRedo: (umRef.current?.redoStack.length ?? 0) > 0,
    publish,
    discardDraft,
    reopen,
    peers: collab?.peers ?? [],
    status,
    chip,
    blocked: collab?.blocked ?? null,
    synced: live ? !!collab?.synced : true,
    peerSelections: peerSel,
    setSelection,
  };
}
